import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient, { getApiUrl } from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { TableSkeleton } from '../components/Skeleton';
import ApiUsageReportControl from '../components/ApiUsageReportControl';
import {
  Key,
  Plus,
  Copy,
  Eye,
  Trash2,
  RefreshCw,
  ExternalLink,
  Shield,
  Users,
  Zap,
  X,
  FileText,
  Edit,
  BarChart3,
  Calendar,
  TrendingUp,
  Activity,
  Mail
} from 'lucide-react';

const ApiSharing = () => {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();

  // Debug: Log every render
  console.log('🔍 ApiSharing component render - Workspace ID:', currentWorkspace?.id, 'Name:', currentWorkspace?.name);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTokenSecret, setShowTokenSecret] = useState({});
  const [usageStats, setUsageStats] = useState({});
  const [selectedToken, setSelectedToken] = useState(null);
  const [activeTab, setActiveTab] = useState('tokens'); // 'tokens', 'documentation', or 'usage'
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingToken, setEditingToken] = useState(null);
  const [editSelectedTemplates, setEditSelectedTemplates] = useState([]);

  // Usage analytics filters
  const [usageFilters, setUsageFilters] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 30 days
    endDate: new Date().toISOString().split('T')[0],
    tokenId: ''
  });

  // Usage analytics sub-tab state
  const [usageSubTab, setUsageSubTab] = useState('data'); // 'data' or 'reports'

  // Form state for creating new token
  const [newToken, setNewToken] = useState({
    token_name: '',
    permissions: ['send_messages', 'view_templates'],
    rate_limit_per_hour: 100,
    rate_limit_per_day: 1000,
    expires_at: '',
    allowed_templates: 'all' // 'all' or array of template names
  });

  const [selectedTemplates, setSelectedTemplates] = useState([]);

  // Fetch API tokens using React Query
  const { data: tokensData, isLoading: loading, error } = useQuery(
    ['apiTokens', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) {
        console.log('No workspace selected, skipping API tokens fetch');
        return { success: true, tokens: [] };
      }

      const headers = { 'x-workspace-id': currentWorkspace.id };
      console.log('🔍 Fetching API tokens with headers:', headers);
      console.log('🔍 Current workspace:', currentWorkspace);

      const response = await apiClient.get('/api/tokens', { headers });
      console.log('✅ API tokens response:', response.data);

      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      onError: (error) => {
        console.error('❌ Error fetching tokens:', error);
        const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to fetch API tokens';
        toast.error(`Failed to fetch API tokens: ${errorMessage}`);
      }
    }
  );

  const tokens = tokensData?.tokens || [];

  // Fetch available templates
  const { data: templatesData } = useQuery(
    ['templates', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) return { templates: [] };
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get('/api/whatsapp/templates', { headers });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id
    }
  );

  const templates = templatesData?.templates || [];

  // Fetch usage analytics
  const { data: usageAnalytics, isLoading: usageLoading, error: usageError } = useQuery(
    ['apiUsageAnalytics', currentWorkspace?.id, usageFilters],
    async () => {
      if (!currentWorkspace?.id) return null;

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const params = {
        startDate: usageFilters.startDate,
        endDate: usageFilters.endDate,
        tokenId: usageFilters.tokenId || undefined
      };

      const response = await apiClient.get('/api/tokens/usage/analytics', { headers, params });
      console.log('✅ Usage analytics response:', response.data);
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id && activeTab === 'usage',
      onError: (err) => {
        console.error('❌ Usage analytics error:', err?.response?.data || err?.message);
        const msg = err?.response?.data?.error || err?.message || 'Failed to load usage analytics';
        toast.error(msg);
      }
    }
  );

  // Sync Facebook templates mutation
  const syncTemplatesMutation = useMutation(
    async () => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.post('/api/whatsapp/templates/sync', {}, { headers });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message || `Synced ${data.saved} templates successfully`);
        queryClient.invalidateQueries(['templates', currentWorkspace?.id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to sync templates');
      }
    }
  );

  // Create token mutation
  const createTokenMutation = useMutation(
    async (tokenData) => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.post('/api/tokens', tokenData, { headers });
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['apiTokens', currentWorkspace?.id]);
        toast.success('API token created successfully');
        setShowCreateModal(false);

        // Reset form
        setNewToken({
          token_name: '',
          permissions: ['send_messages', 'view_templates'],
          rate_limit_per_hour: 100,
          rate_limit_per_day: 1000,
          expires_at: '',
          allowed_templates: 'all'
        });
        setSelectedTemplates([]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to create API token');
      }
    }
  );

  // Update token mutation
  const updateTokenMutation = useMutation(
    async ({ tokenId, tokenData }) => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.put(`/api/tokens/${tokenId}`, tokenData, { headers });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['apiTokens', currentWorkspace?.id]);
        toast.success('API token updated successfully');
        setShowEditModal(false);
        setEditingToken(null);
        setEditSelectedTemplates([]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update API token');
      }
    }
  );

  // Delete token mutation
  const deleteTokenMutation = useMutation(
    async (tokenId) => {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      await apiClient.delete(`/api/tokens/${tokenId}`, { headers });
      return tokenId;
    },
    {
      onSuccess: (tokenId) => {
        queryClient.invalidateQueries(['apiTokens', currentWorkspace?.id]);
        toast.success('API token deleted successfully');

        // Remove usage stats for deleted token
        setUsageStats(prev => {
          const updated = { ...prev };
          delete updated[tokenId];
          return updated;
        });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to delete API token');
      }
    }
  );

  // Fetch usage stats for a token
  const fetchUsageStats = async (tokenId) => {
    if (!currentWorkspace?.id) {
      console.log('No workspace selected, cannot fetch usage stats');
      return;
    }

    try {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get(`/api/tokens/${tokenId}/usage?period=7d`, { headers });

      if (response.data.success) {
        setUsageStats(prev => ({
          ...prev,
          [tokenId]: response.data
        }));
      }
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    }
  };


  // Show loading state while workspace is being loaded
  if (!currentWorkspace) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading workspace...</p>
        </div>
      </div>
    );
  }

  // Create new API token
  const handleCreateToken = (e) => {
    e.preventDefault();

    if (!currentWorkspace?.id) {
      toast.error('Please select a workspace first');
      return;
    }

    // Validate template selection
    if (newToken.allowed_templates !== 'all' && selectedTemplates.length === 0) {
      toast.error('Please select at least one template or choose "Allow all templates"');
      return;
    }

    // Prepare token data with templates
    const tokenData = {
      ...newToken,
      allowed_templates: newToken.allowed_templates === 'all'
        ? 'all'
        : selectedTemplates
    };

    createTokenMutation.mutate(tokenData);
  };

  // Open edit modal
  const handleEditToken = (token) => {
    setEditingToken({
      id: token.id,
      token_name: token.token_name,
      permissions: token.permissions,
      rate_limit_per_hour: token.rate_limit_per_hour,
      rate_limit_per_day: token.rate_limit_per_day,
      allowed_templates: token.allowed_templates || 'all',
      is_active: token.is_active
    });

    // Set selected templates if it's an array
    if (Array.isArray(token.allowed_templates)) {
      setEditSelectedTemplates(token.allowed_templates);
    } else {
      setEditSelectedTemplates([]);
    }

    setShowEditModal(true);
  };

  // Update API token
  const handleUpdateToken = (e) => {
    e.preventDefault();

    if (!currentWorkspace?.id) {
      toast.error('Please select a workspace first');
      return;
    }

    // Validate template selection
    if (editingToken.allowed_templates !== 'all' && editSelectedTemplates.length === 0) {
      toast.error('Please select at least one template or choose "Allow all templates"');
      return;
    }

    const tokenData = {
      token_name: editingToken.token_name,
      permissions: editingToken.permissions,
      rate_limit_per_hour: editingToken.rate_limit_per_hour,
      rate_limit_per_day: editingToken.rate_limit_per_day,
      is_active: editingToken.is_active,
      allowed_templates: editingToken.allowed_templates === 'all' ? 'all' : editSelectedTemplates
    };

    updateTokenMutation.mutate({ tokenId: editingToken.id, tokenData });
  };

  // Delete API token
  const handleDeleteToken = (tokenId) => {
    if (!currentWorkspace?.id) {
      toast.error('Please select a workspace first');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this API token? This action cannot be undone.')) {
      return;
    }

    deleteTokenMutation.mutate(tokenId);
  };

  // Regenerate token secret
  const handleRegenerateSecret = async (tokenId) => {
    if (!currentWorkspace?.id) {
      toast.error('Please select a workspace first');
      return;
    }

    if (!window.confirm('Are you sure you want to regenerate the secret? The old secret will no longer work.')) {
      return;
    }

    try {
      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.post(`/api/tokens/${tokenId}/regenerate`, {}, { headers });

      if (response.data.success) {
        toast.success('Token secret regenerated successfully');
        // Show the new secret temporarily
        setShowTokenSecret(prev => ({ ...prev, [tokenId]: true }));
        // Invalidate queries to reload tokens with new secret
        queryClient.invalidateQueries(['apiTokens', currentWorkspace?.id]);
      }
    } catch (error) {
      console.error('Error regenerating secret:', error);
      toast.error('Failed to regenerate token secret');
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  // Toggle token secret visibility
  const toggleSecretVisibility = (tokenId) => {
    setShowTokenSecret(prev => ({
      ...prev,
      [tokenId]: !prev[tokenId]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (token) => {
    if (!token.is_active) return 'text-red-600 bg-red-100';
    if (token.expires_at && new Date(token.expires_at) < new Date()) return 'text-red-600 bg-red-100';
    return 'text-green-600 bg-green-100';
  };

  const getStatusText = (token) => {
    if (!token.is_active) return 'Inactive';
    if (token.expires_at && new Date(token.expires_at) < new Date()) return 'Expired';
    return 'Active';
  };

  if (loading) {
    return (
      <div>
        <div className="flex items-center space-x-2 mb-6">
          <div className="h-6 w-6 bg-gray-200 animate-pulse rounded" />
          <div className="h-8 w-48 bg-gray-200 animate-pulse rounded" />
        </div>
        <TableSkeleton rows={5} columns={6} />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Key className="h-8 w-8 text-blue-600" />
              API Sharing
            </h1>
            <p className="text-gray-600 mt-2">
              Generate and manage API tokens for external developers to access your WhatsApp messaging platform
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('tokens')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'tokens'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              API Tokens
            </div>
          </button>
          <button
            onClick={() => setActiveTab('usage')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'usage'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Usage Analytics
            </div>
          </button>
          <button
            onClick={() => setActiveTab('documentation')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'documentation'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <div className="flex items-center gap-2">
              <ExternalLink className="h-5 w-5" />
              API Documentation
            </div>
          </button>
        </nav>
      </div>

      {/* API Tokens Tab */}
      {activeTab === 'tokens' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Key className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Tokens</p>
                  <p className="text-2xl font-bold text-gray-900">{tokens.length}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-xl">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Tokens</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tokens.filter(t => getStatusText(t) === 'Active').length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-100 rounded-xl">
                  <FileText className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Restricted Access</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tokens.filter(t => t.allowed_templates !== 'all' && t.allowed_templates).length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* API Tokens Management */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">API Tokens Management</h2>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Create New Token
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading API tokens...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-red-500 mb-4">❌ Error loading API tokens</div>
                <p className="text-gray-600 mb-4">{error?.message || error?.response?.data?.error || 'Failed to load tokens'}</p>
                <button
                  onClick={() => queryClient.invalidateQueries(['apiTokens', currentWorkspace?.id])}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : tokens.length === 0 ? (
              <div className="p-12 text-center">
                <Key className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No API tokens yet</h3>
                <p className="text-gray-600 mb-6">Create your first API token to start sharing your WhatsApp API with external developers</p>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create API Token
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Key</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Template Access</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate Limits</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tokens.map((token) => (
                      <tr key={token.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{token.token_name}</div>
                            <div className="text-sm text-gray-500">Created {new Date(token.created_at).toLocaleDateString()}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                              {token.token_key.substring(0, 12)}...
                            </span>
                            <button
                              onClick={() => copyToClipboard(token.token_key)}
                              className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                              title="Copy API Key"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${token.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {token.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {token.allowed_templates === 'all' || !token.allowed_templates ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              All Templates
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              {Array.isArray(token.allowed_templates) ? token.allowed_templates.length : 0} Templates
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{token.rate_limit_per_hour}/hour</div>
                          <div>{token.rate_limit_per_day}/day</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedToken(token)}
                              className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200 transition-all"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditToken(token)}
                              className="p-2 rounded-lg bg-green-100 text-green-600 hover:bg-green-200 transition-all"
                              title="Edit Token"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleRegenerateSecret(token.id)}
                              className="p-2 rounded-lg bg-orange-100 text-orange-600 hover:bg-orange-200 transition-all"
                              title="Regenerate Secret"
                            >
                              <RefreshCw className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteToken(token.id)}
                              className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-all"
                              title="Delete Token"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Usage Analytics Tab */}
      {activeTab === 'usage' && (
        <>
          {/* Sub-tabs for Usage Analytics */}
          <div className="mb-6 border-b-2 border-gray-200 bg-white rounded-t-xl shadow-sm">
            <nav className="-mb-0.5 flex space-x-8 px-6 pt-4">
              <button
                onClick={() => setUsageSubTab('data')}
                className={`pb-4 px-1 border-b-4 font-semibold text-sm transition-colors flex items-center gap-2 ${usageSubTab === 'data'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <BarChart3 className="h-5 w-5" />
                Usage Data
              </button>
              <button
                onClick={() => setUsageSubTab('reports')}
                className={`pb-4 px-1 border-b-4 font-semibold text-sm transition-colors flex items-center gap-2 ${usageSubTab === 'reports'
                  ? 'border-purple-600 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Mail className="h-5 w-5" />
                Email Reports
              </button>
            </nav>
          </div>

          {/* Email Reports Sub-tab */}
          {usageSubTab === 'reports' && (
            <div className="mt-6">
              <ApiUsageReportControl />
            </div>
          )}

          {/* Usage Data Sub-tab */}
          {usageSubTab === 'data' && (
            <>
              {/* Filters */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Filter Usage Data
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={usageFilters.startDate}
                      onChange={(e) => setUsageFilters({ ...usageFilters, startDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={usageFilters.endDate}
                      onChange={(e) => setUsageFilters({ ...usageFilters, endDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Token</label>
                    <select
                      value={usageFilters.tokenId}
                      onChange={(e) => setUsageFilters({ ...usageFilters, tokenId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Tokens</option>
                      {tokens.map(token => (
                        <option key={token.id} value={token.id}>
                          {token.token_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setUsageFilters({
                          startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                          endDate: new Date().toISOString().split('T')[0],
                          tokenId: ''
                        });
                      }}
                      className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Reset
                    </button>
                  </div>
                </div>
              </div>

              {usageLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading usage analytics...</span>
                </div>
              ) : usageAnalytics ? (
                <>
                  {/* Overview Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                    <div className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Total Requests</p>
                          <p className="text-3xl font-bold text-gray-900 mt-2">
                            {usageAnalytics.overall?.total_requests || 0}
                          </p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-xl">
                          <Activity className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Successful</p>
                          <p className="text-3xl font-bold text-green-600 mt-2">
                            {usageAnalytics.overall?.successful_requests || 0}
                          </p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-xl">
                          <TrendingUp className="h-6 w-6 text-green-600" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {usageAnalytics.overall?.total_requests > 0
                          ? `${Math.round((usageAnalytics.overall.successful_requests / usageAnalytics.overall.total_requests) * 100)}% success rate`
                          : '0% success rate'}
                      </p>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Failed</p>
                          <p className="text-3xl font-bold text-red-600 mt-2">
                            {usageAnalytics.overall?.failed_requests || 0}
                          </p>
                        </div>
                        <div className="p-3 bg-red-100 rounded-xl">
                          <X className="h-6 w-6 text-red-600" />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {usageAnalytics.overall?.total_requests > 0
                          ? `${Math.round((usageAnalytics.overall.failed_requests / usageAnalytics.overall.total_requests) * 100)}% error rate`
                          : '0% error rate'}
                      </p>
                    </div>

                    <div className="bg-white p-6 rounded-xl shadow-sm border hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">Avg Response</p>
                          <p className="text-3xl font-bold text-purple-600 mt-2">
                            {Math.round(usageAnalytics.overall?.avg_response_time || 0)}
                            <span className="text-sm text-gray-500 ml-1">ms</span>
                          </p>
                        </div>
                        <div className="p-3 bg-purple-100 rounded-xl">
                          <Activity className="h-6 w-6 text-purple-600" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Usage by Token */}
                  <div className="bg-white rounded-xl shadow-sm border mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Key className="h-5 w-5 text-blue-600" />
                        Usage by Token
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Requests</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Successful</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Failed</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Response</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Used</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {usageAnalytics.by_token && usageAnalytics.by_token.length > 0 ? (
                            usageAnalytics.by_token.map((tokenUsage) => (
                              <tr key={tokenUsage.api_token_id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm font-medium text-gray-900">{tokenUsage.token_name}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {tokenUsage.requests}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {tokenUsage.successful}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    {tokenUsage.failed}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {Math.round(tokenUsage.avg_response_time || 0)} ms
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {tokenUsage.last_used ? new Date(tokenUsage.last_used).toLocaleString() : 'N/A'}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                No usage data available for the selected filters
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Usage by Endpoint */}
                  <div className="bg-white rounded-xl shadow-sm border mb-6">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <ExternalLink className="h-5 w-5 text-blue-600" />
                        Usage by Endpoint
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endpoint</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requests</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Successful</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Failed</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Response</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {usageAnalytics.by_endpoint && usageAnalytics.by_endpoint.length > 0 ? (
                            usageAnalytics.by_endpoint.map((endpointUsage, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${endpointUsage.method === 'GET' ? 'bg-green-100 text-green-800' :
                                    endpointUsage.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                                      endpointUsage.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-gray-100 text-gray-800'
                                    }`}>
                                    {endpointUsage.method}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-sm font-mono text-gray-900">{endpointUsage.endpoint}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                                  {endpointUsage.requests}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                  {endpointUsage.successful}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                                  {endpointUsage.failed}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {Math.round(endpointUsage.avg_response_time || 0)} ms
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                No endpoint usage data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Usage by Date */}
                  <div className="bg-white rounded-xl shadow-sm border">
                    <div className="px-6 py-4 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-blue-600" />
                        Daily Usage Trends (Last 30 Days)
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Requests</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Successful</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Failed</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Response</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {usageAnalytics.by_date && usageAnalytics.by_date.length > 0 ? (
                            usageAnalytics.by_date.map((dayUsage) => (
                              <tr key={dayUsage.date} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {new Date(dayUsage.date).toLocaleDateString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {dayUsage.requests}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    {dayUsage.successful}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    {dayUsage.failed}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {Math.round(dayUsage.avg_response_time || 0)} ms
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                No daily usage data available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                  <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Usage Data Yet</h3>
                  <p className="text-gray-600">API usage statistics will appear here once your tokens start being used.</p>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* API Documentation Tab */}
      {activeTab === 'documentation' && (
        <>
          {/* Templates Info Banner */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-green-600" />
                <div>
                  <h3 className="font-semibold text-green-900">Facebook Templates Available</h3>
                  <p className="text-sm text-green-700">
                    {templates.length} templates cached and ready to use via API
                  </p>
                </div>
              </div>
              <button
                onClick={() => syncTemplatesMutation.mutate()}
                disabled={syncTemplatesMutation.isLoading}
                className="bg-white text-green-700 border-2 border-green-600 px-4 py-2 rounded-lg hover:bg-green-50 flex items-center gap-2 transition-all disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${syncTemplatesMutation.isLoading ? 'animate-spin' : ''}`} />
                <span>{syncTemplatesMutation.isLoading ? 'Syncing...' : 'Sync Templates'}</span>
              </button>
            </div>
          </div>

          {/* API Overview */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-blue-600" />
              WhatsApp API Overview
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white p-4 rounded-lg border">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Key className="h-4 w-4 text-green-600" />
                  Authentication
                </h3>
                <div className="space-y-2">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Base URL:</label>
                    <div className="bg-gray-100 p-2 rounded font-mono text-sm mt-1">
                      {getApiUrl()}/api/public
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Header Format:</label>
                    <div className="bg-gray-100 p-2 rounded font-mono text-sm mt-1">
                      Authorization: Bearer api_key:api_secret
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg border">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-orange-600" />
                  Main Features
                </h3>
                <ul className="space-y-1 text-sm text-gray-700">
                  <li>• Send WhatsApp messages via Facebook templates</li>
                  <li>• ✨ Contact name & email capture for better tracking</li>
                  <li>• View available templates</li>
                  <li>• Track message delivery status</li>
                  <li>• Monitor API usage and limits</li>
                  <li>• Rate limiting and security</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-100 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">🚀 Quick Start</h4>
              <p className="text-blue-800 text-sm mb-3">
                External developers can integrate your WhatsApp messaging in 3 simple steps:
              </p>
              <ol className="text-blue-800 text-sm space-y-1 ml-4">
                <li>1. Get API credentials from workspace admin</li>
                <li>2. View available Facebook templates via <code className="bg-blue-200 px-1 rounded">GET /templates</code></li>
                <li>3. Send messages using <code className="bg-blue-200 px-1 rounded">POST /send-message</code> with optional contact names</li>
              </ol>
            </div>
          </div>

          {/* API Endpoints */}
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ExternalLink className="h-5 w-5 text-blue-600" />
              Available API Endpoints
            </h2>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endpoint</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Facebook Templates</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        GET
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">/api/public/info</td>
                    <td className="px-6 py-4 text-sm text-gray-900">Get API token information and permissions</td>
                    <td className="px-6 py-4 text-sm text-gray-500">-</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        GET
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">/api/public/templates?days=all</td>
                    <td className="px-6 py-4 text-sm text-gray-900">List all available Facebook templates</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">✅ Shows all approved templates<br /><span className="text-xs text-gray-500">Query params: days=all|30|90|365</span></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        POST
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">/api/public/send-message</td>
                    <td className="px-6 py-4 text-sm text-gray-900">Send WhatsApp message using Facebook template</td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">✅ Uses template_name parameter<br /><span className="text-xs text-blue-600">✨ Now supports contact_name & contact_email</span></td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        GET
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">/api/public/message/{'{id}'}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">Get message delivery status</td>
                    <td className="px-6 py-4 text-sm text-gray-500">-</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        GET
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">/api/public/usage</td>
                    <td className="px-6 py-4 text-sm text-gray-900">Get API usage statistics and limits</td>
                    <td className="px-6 py-4 text-sm text-gray-500">-</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-4 p-4 bg-green-50 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2">📱 Facebook Templates Integration</h4>
              <p className="text-green-800 text-sm mb-3">
                Your API automatically provides access to all Facebook-approved WhatsApp templates in your workspace.
                External users can browse templates and send personalized messages using template parameters.
              </p>
            </div>
          </div>

          {/* API Examples */}
          <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              API Usage Examples
            </h2>

            {/* Send Message Examples */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-green-600" />
                  Send WhatsApp Message
                </h3>

                <div className="space-y-4">
                  {/* Basic Example */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Basic Message (Required Parameters)</h4>
                    <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm overflow-x-auto">
                      <div className="text-blue-400">curl -X POST "{getApiUrl()}/api/public/send-message" \</div>
                      <div className="text-blue-400">  -H "Authorization: Bearer api_key:api_secret" \</div>
                      <div className="text-blue-400">  -H "Content-Type: application/json" \</div>
                      <div className="text-blue-400">  -d '{'{'}'</div>
                      <div className="text-yellow-300">    "to": "919876543210",</div>
                      <div className="text-yellow-300">    "template_name": "hello_world",</div>
                      <div className="text-yellow-300">    "parameters": {'{'}'</div>
                      <div className="text-yellow-300">      "param_1": "John"</div>
                      <div className="text-yellow-300">    {'}'}'</div>
                      <div className="text-blue-400">  {'}'}'</div>
                    </div>
                  </div>

                  {/* With Contact Name Example */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">✨ With Contact Name & Token (New Feature)</h4>
                    <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm overflow-x-auto">
                      <div className="text-blue-400">curl -X POST "{getApiUrl()}/api/public/send-message" \</div>
                      <div className="text-blue-400">  -H "Authorization: Bearer api_key:api_secret" \</div>
                      <div className="text-blue-400">  -H "Content-Type: application/json" \</div>
                      <div className="text-blue-400">  -d '{'{'}'</div>
                      <div className="text-yellow-300">    "to": "919876543210",</div>
                      <div className="text-yellow-300">    "template_name": "welcome_message",</div>
                      <div className="text-yellow-300">    "contact_name": "Alice Johnson",</div>
                      <div className="text-yellow-300">    "contact_email": "alice@example.com",</div>
                      <div className="text-yellow-300">    "token": "ca-oms",</div>
                      <div className="text-yellow-300">    "parameters": {'{'}'</div>
                      <div className="text-yellow-300">      "param_1": "Alice"</div>
                      <div className="text-yellow-300">    {'}'}'</div>
                      <div className="text-blue-400">  {'}'}'</div>
                    </div>
                  </div>

                  {/* Query Parameters Example */}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">🔗 Using Query Parameters</h4>
                    <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm overflow-x-auto">
                      <div className="text-blue-400">curl -X POST "{getApiUrl()}/api/public/send-message?contact_name=Bob%20Smith&contact_email=bob@example.com&token=ecommerce-app" \</div>
                      <div className="text-blue-400">  -H "Authorization: Bearer api_key:api_secret" \</div>
                      <div className="text-blue-400">  -H "Content-Type: application/json" \</div>
                      <div className="text-blue-400">  -d '{'{'}'</div>
                      <div className="text-yellow-300">    "to": "919876543210",</div>
                      <div className="text-yellow-300">    "template_name": "order_confirmation",</div>
                      <div className="text-yellow-300">    "parameters": {'{'}'</div>
                      <div className="text-yellow-300">      "param_1": "Order #12345"</div>
                      <div className="text-yellow-300">    {'}'}'</div>
                      <div className="text-blue-400">  {'}'}'</div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                  <h4 className="font-semibold text-yellow-900 mb-2">📋 Send Message Parameters</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <h5 className="font-medium text-yellow-900 mb-2">Required:</h5>
                      <ul className="space-y-1 text-yellow-800">
                        <li><code className="bg-yellow-200 px-1 rounded">to</code> - Phone number (with country code)</li>
                        <li><code className="bg-yellow-200 px-1 rounded">template_name</code> - Facebook template name</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium text-yellow-900 mb-2">Optional:</h5>
                      <ul className="space-y-1 text-yellow-800">
                        <li><code className="bg-yellow-200 px-1 rounded">contact_name</code> - Recipient's name</li>
                        <li><code className="bg-yellow-200 px-1 rounded">contact_email</code> - Recipient's email</li>
                        <li><code className="bg-yellow-200 px-1 rounded">token</code> - Custom token name for identification</li>
                        <li><code className="bg-yellow-200 px-1 rounded">parameters</code> - Template parameters</li>
                        <li><code className="bg-yellow-200 px-1 rounded">language</code> - Template language (default: en_US)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Example */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <ExternalLink className="h-4 w-4 text-blue-600" />
                  Response Format
                </h3>
                <div className="bg-gray-900 text-green-400 p-3 rounded font-mono text-sm overflow-x-auto">
                  <div className="text-gray-300">{'{'}</div>
                  <div className="text-yellow-300">  "success": true,</div>
                  <div className="text-yellow-300">  "message": "Message sent successfully",</div>
                  <div className="text-yellow-300">  "message_id": "wamid.xxx",</div>
                  <div className="text-yellow-300">  "recipient": "919876543210",</div>
                  <div className="text-yellow-300">  "template": "welcome_message",</div>
                  <div className="text-yellow-300">  "contact_name": "Alice Johnson",</div>
                  <div className="text-yellow-300">  "contact_email": "alice@example.com",</div>
                  <div className="text-yellow-300">  "token_name": "ca-oms"</div>
                  <div className="text-gray-300">{'}'}</div>
                </div>
              </div>

              {/* Benefits */}
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">🎯 Enhanced Tracking Benefits</h4>
                <ul className="space-y-1 text-green-800 text-sm">
                  <li>✅ <strong>Better Contact Tracking:</strong> See contact names in your message history instead of just phone numbers</li>
                  <li>✅ <strong>API Client Identification:</strong> Identify which external client/token sent each message</li>
                  <li>✅ <strong>Auto Contact Creation:</strong> Contacts are automatically created/updated in your system</li>
                  <li>✅ <strong>Flexible Input:</strong> Provide names and tokens via request body or query parameters</li>
                  <li>✅ <strong>Smart Resolution:</strong> System auto-looks up existing contacts and uses actual token names if not provided</li>
                  <li>✅ <strong>Backward Compatible:</strong> Existing API calls continue to work without changes</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Token Details Modal */}
      {selectedToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Token Details: {selectedToken.token_name}</h3>
              <button
                onClick={() => setSelectedToken(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={selectedToken.token_key}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(selectedToken.token_key)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API Secret</label>
                <div className="flex items-center gap-2">
                  <input
                    type="password"
                    value={selectedToken.token_secret}
                    readOnly
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 font-mono"
                  />
                  <button
                    onClick={() => copyToClipboard(selectedToken.token_secret)}
                    className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Permissions</label>
                <div className="flex flex-wrap gap-1">
                  {selectedToken.permissions.map((permission) => (
                    <span key={permission} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {permission}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rate Limits</label>
                <div className="text-sm text-gray-600">
                  <div>{selectedToken.rate_limit_per_hour}/hour</div>
                  <div>{selectedToken.rate_limit_per_day}/day</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Used</label>
                <div className="text-sm text-gray-600">
                  {selectedToken.last_used_at
                    ? new Date(selectedToken.last_used_at).toLocaleString()
                    : 'Never'
                  }
                </div>
              </div>
            </div>

            {/* Allowed Templates */}
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <label className="block text-sm font-medium text-green-900 mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Allowed Templates
              </label>
              {selectedToken.allowed_templates === 'all' || !selectedToken.allowed_templates ? (
                <p className="text-sm text-green-800">✅ This token has access to <strong>all templates</strong> in the workspace</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-green-800">
                    This token has access to <strong>{Array.isArray(selectedToken.allowed_templates) ? selectedToken.allowed_templates.length : 0} specific template(s)</strong>:
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {Array.isArray(selectedToken.allowed_templates) && selectedToken.allowed_templates.map((templateName, index) => (
                      <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full font-medium">
                        {templateName}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Contact Name Feature */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <label className="block text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                <Users className="h-4 w-4" />
                ✨ New: Enhanced Tracking
              </label>
              <div className="space-y-2">
                <p className="text-sm text-blue-800">
                  This API now supports <strong>contact name, email, and token identification</strong> for better message tracking:
                </p>
                <ul className="text-sm text-blue-800 space-y-1 ml-4">
                  <li>• Add <code className="bg-blue-200 px-1 rounded">contact_name</code> and <code className="bg-blue-200 px-1 rounded">contact_email</code> to requests</li>
                  <li>• Add <code className="bg-blue-200 px-1 rounded">token</code> to identify which client sent the message</li>
                  <li>• Contacts are automatically created/updated in your system</li>
                  <li>• See contact names and token names in message history</li>
                  <li>• Works with both request body and query parameters</li>
                </ul>
              </div>
            </div>

            {/* API Usage Instructions */}
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-900 mb-3">🔧 Authentication</h4>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Authorization Header</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={`Bearer ${selectedToken.token_key}:${selectedToken.token_secret}`}
                        readOnly
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white font-mono"
                      />
                      <button
                        onClick={() => copyToClipboard(`Bearer ${selectedToken.token_key}:${selectedToken.token_secret}`)}
                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-3">📱 Example: Send WhatsApp Message</h4>
                <div className="bg-gray-800 text-green-400 p-3 rounded-lg font-mono text-sm overflow-x-auto">
                  <div>curl -X POST "{getApiUrl()}/api/public/send-message" \</div>
                  <div className="ml-4">-H "Authorization: Bearer {selectedToken.token_key}:{selectedToken.token_secret}" \</div>
                  <div className="ml-4">-H "Content-Type: application/json" \</div>
                  <div className="ml-4">-d '{JSON.stringify({
                    to: "+1234567890",
                    template_name: "hello_world",
                    contact_name: "John Doe",
                    contact_email: "john@example.com",
                    token: "ca-oms",
                    parameters: { param_1: "John", param_2: "Doe" },
                    language: "en_US"
                  }, null, 2)}'</div>
                </div>
              </div>

              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="text-sm font-medium text-green-900 mb-3">📋 Example: Get Available Templates</h4>
                <div className="bg-gray-800 text-green-400 p-3 rounded-lg font-mono text-sm overflow-x-auto">
                  <div>curl -X GET "{getApiUrl()}/api/public/templates" \</div>
                  <div className="ml-4">-H "Authorization: Bearer {selectedToken.token_key}:{selectedToken.token_secret}"</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Create API Token</h2>

            <form onSubmit={handleCreateToken}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Token Name
                  </label>
                  <input
                    type="text"
                    value={newToken.token_name}
                    onChange={(e) => setNewToken({ ...newToken, token_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., My App Integration"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Permissions
                  </label>
                  <div className="space-y-2">
                    {['send_messages', 'view_templates'].map((permission) => (
                      <label key={permission} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newToken.permissions.includes(permission)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewToken({
                                ...newToken,
                                permissions: [...newToken.permissions, permission]
                              });
                            } else {
                              setNewToken({
                                ...newToken,
                                permissions: newToken.permissions.filter(p => p !== permission)
                              });
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700 capitalize">
                          {permission.replace('_', ' ')}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rate Limit (per hour)
                    </label>
                    <input
                      type="number"
                      value={newToken.rate_limit_per_hour}
                      onChange={(e) => setNewToken({ ...newToken, rate_limit_per_hour: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="1"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Rate Limit (per day)
                    </label>
                    <input
                      type="number"
                      value={newToken.rate_limit_per_day}
                      onChange={(e) => setNewToken({ ...newToken, rate_limit_per_day: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="1"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expires At (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={newToken.expires_at}
                    onChange={(e) => setNewToken({ ...newToken, expires_at: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Template Access Control */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      Template Access Control
                    </label>
                    <span className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded">
                      {templates.length} templates available
                    </span>
                  </div>

                  <p className="text-xs text-gray-500 mb-4 bg-yellow-50 border border-yellow-200 rounded p-2">
                    💡 Control which Facebook templates this API token can access. External developers will only see and use the allowed templates.
                  </p>

                  <div className="space-y-3">
                    <label className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
                      <input
                        type="radio"
                        checked={newToken.allowed_templates === 'all'}
                        onChange={() => {
                          setNewToken({ ...newToken, allowed_templates: 'all' });
                          setSelectedTemplates([]);
                        }}
                        className="mr-3 text-green-600 focus:ring-green-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">All Templates</span>
                        <p className="text-xs text-gray-600">Token can access all current and future templates</p>
                      </div>
                    </label>

                    <label className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors">
                      <input
                        type="radio"
                        checked={newToken.allowed_templates !== 'all'}
                        onChange={() => setNewToken({ ...newToken, allowed_templates: 'specific' })}
                        className="mr-3 text-yellow-600 focus:ring-yellow-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-900">Specific Templates</span>
                        <p className="text-xs text-gray-600">Choose exactly which templates to share</p>
                      </div>
                    </label>

                    {newToken.allowed_templates !== 'all' && (
                      <div className="ml-6 mt-3 p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border-2 border-blue-200 max-h-64 overflow-y-auto">
                        {templates.length === 0 ? (
                          <div className="text-center py-4">
                            <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-sm text-gray-500">No templates available</p>
                            <p className="text-xs text-gray-400">Please create templates first</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between mb-3 pb-2 border-b border-blue-200">
                              <span className="text-xs font-semibold text-gray-700">
                                {selectedTemplates.length} of {templates.length} selected
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  if (selectedTemplates.length === templates.length) {
                                    setSelectedTemplates([]);
                                  } else {
                                    setSelectedTemplates(templates.map(t => t.name));
                                  }
                                }}
                                className="text-xs text-blue-600 hover:text-blue-700 font-semibold underline"
                              >
                                {selectedTemplates.length === templates.length ? 'Deselect All' : 'Select All'}
                              </button>
                            </div>
                            {templates.map((template) => (
                              <label key={template.id} className="flex items-start bg-white hover:bg-blue-50 p-3 rounded-lg transition-colors border border-gray-200 hover:border-blue-300 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={selectedTemplates.includes(template.name)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedTemplates([...selectedTemplates, template.name]);
                                    } else {
                                      setSelectedTemplates(selectedTemplates.filter(t => t !== template.name));
                                    }
                                  }}
                                  className="mt-0.5 mr-3 text-blue-600 focus:ring-blue-500 rounded"
                                />
                                <div className="flex-1">
                                  <div className="text-sm font-semibold text-gray-900">{template.name}</div>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 mr-1">
                                      {template.language}
                                    </span>
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded ${template.status === 'APPROVED' ? 'bg-green-100 text-green-700' : 'bg-gray-100'
                                      }`}>
                                      {template.status}
                                    </span>
                                  </div>
                                </div>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Create Token
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Token Modal */}
      {showEditModal && editingToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-t-2xl p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
                    <Edit className="h-6 w-6 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-white">Edit API Token</h2>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-white hover:bg-white/20 rounded-lg p-2 transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <form onSubmit={handleUpdateToken}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Token Name
                    </label>
                    <input
                      type="text"
                      value={editingToken.token_name}
                      onChange={(e) => setEditingToken({ ...editingToken, token_name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Permissions
                    </label>
                    <div className="space-y-2">
                      {['send_messages', 'view_templates'].map((permission) => (
                        <label key={permission} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={editingToken.permissions.includes(permission)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setEditingToken({
                                  ...editingToken,
                                  permissions: [...editingToken.permissions, permission]
                                });
                              } else {
                                setEditingToken({
                                  ...editingToken,
                                  permissions: editingToken.permissions.filter(p => p !== permission)
                                });
                              }
                            }}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 capitalize">
                            {permission.replace('_', ' ')}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rate Limit (per hour)
                      </label>
                      <input
                        type="number"
                        value={editingToken.rate_limit_per_hour}
                        onChange={(e) => setEditingToken({ ...editingToken, rate_limit_per_hour: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Rate Limit (per day)
                      </label>
                      <input
                        type="number"
                        value={editingToken.rate_limit_per_day}
                        onChange={(e) => setEditingToken({ ...editingToken, rate_limit_per_day: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingToken.is_active}
                        onChange={(e) => setEditingToken({ ...editingToken, is_active: e.target.checked })}
                        className="mr-2 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Token is active</span>
                    </label>
                  </div>

                  {/* Template Access Control */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                        <FileText className="h-4 w-4 text-blue-600" />
                        Template Access Control
                      </label>
                      <span className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded">
                        {templates.length} templates available
                      </span>
                    </div>

                    <p className="text-xs text-gray-500 mb-4 bg-yellow-50 border border-yellow-200 rounded p-2">
                      💡 Update which Facebook templates this API token can access.
                    </p>

                    <div className="space-y-3">
                      <label className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
                        <input
                          type="radio"
                          checked={editingToken.allowed_templates === 'all'}
                          onChange={() => {
                            setEditingToken({ ...editingToken, allowed_templates: 'all' });
                            setEditSelectedTemplates([]);
                          }}
                          className="mr-3 text-green-600 focus:ring-green-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">All Templates</span>
                          <p className="text-xs text-gray-600">Token can access all current and future templates</p>
                        </div>
                      </label>

                      <label className="flex items-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg cursor-pointer hover:bg-yellow-100 transition-colors">
                        <input
                          type="radio"
                          checked={editingToken.allowed_templates !== 'all'}
                          onChange={() => setEditingToken({ ...editingToken, allowed_templates: 'specific' })}
                          className="mr-3 text-yellow-600 focus:ring-yellow-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Specific Templates</span>
                          <p className="text-xs text-gray-600">Choose exactly which templates to share</p>
                        </div>
                      </label>

                      {editingToken.allowed_templates !== 'all' && (
                        <div className="ml-6 mt-3 p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border-2 border-blue-200 max-h-64 overflow-y-auto">
                          {templates.length === 0 ? (
                            <div className="text-center py-4">
                              <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-500">No templates available</p>
                              <p className="text-xs text-gray-400">Please create templates first</p>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between mb-3 pb-2 border-b border-blue-200">
                                <span className="text-xs font-semibold text-gray-700">
                                  {editSelectedTemplates.length} of {templates.length} selected
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (editSelectedTemplates.length === templates.length) {
                                      setEditSelectedTemplates([]);
                                    } else {
                                      setEditSelectedTemplates(templates.map(t => t.name));
                                    }
                                  }}
                                  className="text-xs text-blue-600 hover:text-blue-700 font-semibold underline"
                                >
                                  {editSelectedTemplates.length === templates.length ? 'Deselect All' : 'Select All'}
                                </button>
                              </div>
                              {templates.map((template) => (
                                <label key={template.id} className="flex items-start bg-white hover:bg-blue-50 p-3 rounded-lg transition-colors border border-gray-200 hover:border-blue-300 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={editSelectedTemplates.includes(template.name)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setEditSelectedTemplates([...editSelectedTemplates, template.name]);
                                      } else {
                                        setEditSelectedTemplates(editSelectedTemplates.filter(t => t !== template.name));
                                      }
                                    }}
                                    className="mt-0.5 mr-3 text-blue-600 focus:ring-blue-500 rounded"
                                  />
                                  <div className="flex-1">
                                    <div className="text-sm font-semibold text-gray-900">{template.name}</div>
                                    <div className="text-xs text-gray-500 mt-0.5">
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 mr-1">
                                        {template.language}
                                      </span>
                                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded ${template.status === 'APPROVED' ? 'bg-green-100 text-green-700' : 'bg-gray-100'
                                        }`}>
                                        {template.status}
                                      </span>
                                    </div>
                                  </div>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-6 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 flex items-center gap-2 font-medium shadow-md hover:shadow-lg transition-all"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Update Token</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiSharing;
